<template>
  <div class="hot-ranking-container">
    <div class="ranking-header">
      <h1 class="title">🔥 热门游戏排行榜</h1>
      <div class="period-tabs">
        <el-radio-group v-model="currentPeriod" @change="loadRanking">
          <el-radio-button label="today">今日</el-radio-button>
          <el-radio-button label="week">本周</el-radio-button>
          <el-radio-button label="month">本月</el-radio-button>
          <el-radio-button label="all">总榜</el-radio-button>
        </el-radio-group>
      </div>
    </div>

    <div class="ranking-content" v-loading="loading">
      <div class="ranking-list" v-if="rankingList.length > 0">
        <div 
          v-for="item in rankingList" 
          :key="item.id"
          class="ranking-item"
          :class="{'top-three': item.rank <= 3}"
          @click="goToDetail(item.id)"
        >
          <div class="rank-badge" :class="`rank-${item.rank}`">
            <span v-if="item.rank <= 3" class="medal">{{ getMedal(item.rank) }}</span>
            <span v-else class="rank-number">{{ item.rank }}</span>
          </div>
          
          <div class="game-cover">
            <el-image :src="item.cover" fit="cover" class="cover-img">
              <div slot="error" class="image-slot">
                <i class="el-icon-picture-outline"></i>
              </div>
            </el-image>
          </div>
          
          <div class="game-info">
            <h3 class="game-title">{{ item.title }}</h3>
            <div class="game-meta">
              <el-tag size="mini" :type="item.download_type === 'free' ? 'success' : 'danger'">
                {{ item.download_type === 'free' ? '免费' : 'VIP' }}
              </el-tag>
              <span class="update-time">{{ formatDate(item.game_update_time) }}</span>
            </div>
          </div>
        </div>
      </div>

      <el-empty v-else description="暂无排行数据" />
    </div>
  </div>
</template>

<script>
import { getHotRanking } from '@/api';

export default {
  name: 'HotRanking',
  data() {
    return {
      currentPeriod: 'week',
      rankingList: [],
      loading: false
    };
  },
  mounted() {
    this.loadRanking();
  },
  methods: {
    async loadRanking() {
      this.loading = true;
      try {
        const res = await getHotRanking({
          period: this.currentPeriod,
          limit: 20
        });
        
        if (res.code === 200) {
          this.rankingList = res.data.list || [];
        } else {
          this.$message.error(res.message || '获取排行榜失败');
        }
      } catch (error) {
        console.error('获取排行榜失败:', error);
        this.$message.error('获取排行榜失败');
      } finally {
        this.loading = false;
      }
    },
    
    getMedal(rank) {
      const medals = ['🥇', '🥈', '🥉'];
      return medals[rank - 1] || rank;
    },
    
    formatDate(dateStr) {
      if (!dateStr) return '';
      const date = new Date(dateStr);
      return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
    },
    
    goToDetail(id) {
      this.$router.push(`/gameMqDetail/${id}`);
    }
  }
};
</script>

<style lang="scss" scoped>
@import "@/styles/dark-theme.scss";

.hot-ranking-container {
  max-width: 1200px;
  margin: 0 auto;
  padding: 40px 20px;
  min-height: 100vh;
  background: $dark;
}

.ranking-header {
  text-align: center;
  margin-bottom: 48px;
}

.title {
  font-size: 42px;
  font-weight: 700;
  color: white;
  margin-bottom: 24px;
  text-shadow: 0 2px 8px rgba(0, 0, 0, 0.3);
}

.period-tabs {
  display: flex;
  justify-content: center;

  :deep(.el-radio-group) {
    background: $card;
    border-radius: $radius-xl;
    padding: 4px;
    border: $border-lighter;
  }

  :deep(.el-radio-button__inner) {
    background: transparent;
    border: none;
    color: $slate-300;
    padding: 10px 24px;
    border-radius: $radius-md;
    font-weight: 500;
    transition: $transition-normal;
  }

  :deep(.el-radio-button__orig-radio:checked + .el-radio-button__inner) {
    background: $gradient-primary;
    color: white;
    box-shadow: $shadow-sm;
  }

  :deep(.el-radio-button:first-child .el-radio-button__inner) {
    border-radius: $radius-md;
  }

  :deep(.el-radio-button:last-child .el-radio-button__inner) {
    border-radius: $radius-md;
  }
}

.ranking-content {
  min-height: 400px;
}

.ranking-list {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.ranking-item {
  display: flex;
  align-items: center;
  @include card-style;
  padding: 24px;
  transition: $transition-normal;
  cursor: pointer;

  &:hover {
    transform: translateY(-4px);
    box-shadow: $shadow-primary;
    border-color: rgba($primary, 0.5);
  }
}

.ranking-item.top-three {
  background: linear-gradient(135deg, rgba(251, 191, 36, 0.1) 0%, $card 100%);
  border: 2px solid rgba(251, 191, 36, 0.5);
  box-shadow: 0 4px 20px rgba(251, 191, 36, 0.2);
}

.rank-badge {
  flex-shrink: 0;
  width: 60px;
  height: 60px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 32px;
  font-weight: 700;
  margin-right: 24px;
}

.rank-badge.rank-1 .medal {
  font-size: 52px;
  filter: drop-shadow(0 2px 8px rgba(255, 215, 0, 0.5));
}

.rank-badge.rank-2 .medal,
.rank-badge.rank-3 .medal {
  font-size: 46px;
  filter: drop-shadow(0 2px 6px rgba(192, 192, 192, 0.5));
}

.rank-number {
  color: $slate-400;
  font-size: 28px;
}

.game-cover {
  flex-shrink: 0;
  width: 120px;
  height: 120px;
  border-radius: $radius-md;
  overflow: hidden;
  margin-right: 24px;
  border: $border-lighter;
}

.cover-img {
  width: 100%;
  height: 100%;
}

.image-slot {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100%;
  background: rgba(255, 255, 255, 0.05);
  color: $slate-400;
  font-size: 32px;
}

.game-info {
  flex: 1;
  min-width: 0;
}

.game-title {
  font-size: 18px;
  font-weight: 600;
  color: white;
  margin: 0 0 12px 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.game-meta {
  display: flex;
  align-items: center;
  gap: 12px;

  :deep(.el-tag) {
    border-radius: $radius-sm;
    border: none;
    font-weight: 500;
  }

  :deep(.el-tag--success) {
    background: rgba(103, 194, 58, 0.2);
    color: #67c23a;
  }

  :deep(.el-tag--danger) {
    background: rgba(245, 108, 108, 0.2);
    color: #f56c6c;
  }
}

.update-time {
  font-size: 14px;
  color: $slate-400;
}

:deep(.el-empty) {
  background: transparent;

  .el-empty__description p {
    color: $slate-400;
  }

  .el-empty__image svg {
    fill: $slate-600;
  }
}

/* 移动端适配 */
@media (max-width: 768px) {
  .hot-ranking-container {
    padding: 24px 16px;
  }
  
  .title {
    font-size: 28px;
  }
  
  .ranking-item {
    flex-wrap: wrap;
    padding: 16px;
  }
  
  .rank-badge {
    width: 40px;
    height: 40px;
    font-size: 24px;
    margin-right: 12px;
  }

  .rank-badge.rank-1 .medal {
    font-size: 36px;
  }

  .rank-badge.rank-2 .medal,
  .rank-badge.rank-3 .medal {
    font-size: 32px;
  }
  
  .game-cover {
    width: 80px;
    height: 80px;
    margin-right: 16px;
  }
  
  .game-title {
    font-size: 16px;
  }

  .period-tabs :deep(.el-radio-button__inner) {
    padding: 8px 16px;
    font-size: 14px;
  }
}
</style>
